import React, { useEffect, useMemo, useRef, useState } from 'react';

import { SortingIcon } from 'app/components/shared/icons';
import { useFormikContext } from 'formik';
import { AnimatePresence } from 'framer-motion';
import useOnClickOutside from 'hooks/click-outside';
import { ThemeProvider } from 'styled-components';

import {
  ResourceSubmissionTypeSelectCurrentOption,
  ResourceSubmissionTypeSelectOptionContainer,
  ResourceSubmissionTypeSelectOptionLabel,
  ResourceSubmissionTypeSelectOptionsContainer,
  ResourceSubmissionTypeSelectPlaceholder,
  ResourceSubmissionTypeSelectWrapper,
} from './styled';

interface IResourceSubmissionTypeSelectOption {
  value: string;
  label: string;
}

interface ResourceSubmissionTypeSelectProps {
  options: IResourceSubmissionTypeSelectOption[];
  name: string;
  placeholder?: string;
  preselectedValue?: IResourceSubmissionTypeSelectOption;
  onChange?: (value: IResourceSubmissionTypeSelectOption) => void;
}

export default function ResourceSubmissionTypeSelect({
  options,
  name,
  placeholder,
  preselectedValue,
  onChange,
}: ResourceSubmissionTypeSelectProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const formikContext = useFormikContext();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<IResourceSubmissionTypeSelectOption>(
    preselectedValue || null
  );

  useOnClickOutside(wrapperRef, () => {
    if (isOpen) {
      formikContext.setFieldTouched(name, true, false);
    }
    setIsOpen(false);
  });

  const handleWrapperClick = () => {
    if (isOpen) return;

    setIsOpen(true);
  };

  const handleOptionClick = (option: IResourceSubmissionTypeSelectOption) => {
    if (name && formikContext) {
      formikContext.setFieldValue(name, option.value);
    }
    onChange?.(option);
    setSelectedOption(option);
    setIsOpen(false);
  };

  const isError = useMemo<boolean>(() => {
    return formikContext.errors[name] && formikContext.touched[name];
  }, [formikContext.errors[name], formikContext.touched[name], name]);

  return (
    <ThemeProvider theme={{ isError }}>
      <ResourceSubmissionTypeSelectWrapper onClick={handleWrapperClick} ref={wrapperRef}>
        <input type="hidden" name={name} value={formikContext.values[name]} />
        {selectedOption ? (
          <ResourceSubmissionTypeSelectCurrentOption>
            {selectedOption?.label}
          </ResourceSubmissionTypeSelectCurrentOption>
        ) : (
          <ResourceSubmissionTypeSelectPlaceholder>
            {placeholder}
          </ResourceSubmissionTypeSelectPlaceholder>
        )}
        <SortingIcon />
        <AnimatePresence>
          {isOpen ? (
            <ResourceSubmissionTypeSelectOptionsContainer>
              {options?.map((option) => (
                <ResourceSubmissionTypeSelectOptionContainer
                  key={option.value}
                  onClick={() => handleOptionClick(option)}
                >
                  <ResourceSubmissionTypeSelectOptionLabel>
                    {option.label}
                  </ResourceSubmissionTypeSelectOptionLabel>
                </ResourceSubmissionTypeSelectOptionContainer>
              ))}
            </ResourceSubmissionTypeSelectOptionsContainer>
          ) : null}
        </AnimatePresence>
      </ResourceSubmissionTypeSelectWrapper>
    </ThemeProvider>
  );
}
